import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, FileText, Hand, Loader2, Plus, Send } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { cleanupUserPrompt } from '../../lib/cleanupPromptSpelling';
import { formatOpenQuestionsList, inferCreatePackViaCorpus, lookupTaskCorpus, reviewCreateTicketViaCorpus, type CreateTicketReview } from '../../lib/taskChatCorpus';
import {
  localDateAtHour,
  resolveCreateDue,
  toDateOnly,
  type ScheduleBoardContext,
  type ScheduleHorizon,
} from '../../lib/taskScheduleContext';
import {
  askPromptForStep,
  acceptsForceCreate,
  emptyCreatePack,
  extractClarifyNote,
  formatGotSummary,
  formatSimilarTasksPushback,
  mergeCreatePack,
  missingCreateFields,
  nextMissingCreateStep,
  parseCreateDump,
  pickSimilarTasks,
  resolveEpicFromHint,
  type CreatePack,
  type SimilarOpenTask,
} from '../../lib/taskCreateIntake';
import { reportUiError } from '../../sentry';
import {
  emptyTaskEval,
  EPIC_PLACEMENT_LABELS,
  EPIC_PLACEMENTS,
  isHighValueStrategyScore,
  isManualTaskSource,
  normalizeEpicPlacement,
  readTaskEval,
  readTaskValueEvidence,
  slugifyEpicTitle,
  STRATEGY_SCORE_DESCRIPTIONS,
  STRATEGY_SCORE_LABELS,
  strategyScoreFromAxes,
  withTaskEval,
  withTaskValueEvidence,
  type EpicPlacement,
  type NetworkingTaskRow,
  type TaskEval,
  type TaskEvalMessage,
  type TaskValueAxisScores,
  type TaskValueEvidence,
  type TaskValueResource,
  type WorkEpicRow,
} from '../../lib/networkingTasks';
import { ModalBody, ModalFooter, ModalHeader, ModalOverlay, ModalPanel } from '../Common/ModalShell';

const AXIS_LABELS = {
  economic: { short: '$', long: 'Economic' },
  goals: { short: 'Goals', long: 'Goals' },
  network: { short: 'Network', long: 'Network' },
} as const;

const fieldClass =
  'w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-violet-500';

const labelClass = 'block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1.5';

type UiTab = 'chat' | 'details';
/**
 * Create = planning meeting in chat (smart intake).
 * seed/value/outcome/priority can arrive in one dump → only ask missing →
 * ai_questions (OpenAI clarifiers) → dup_check → placement → score → confirm.
 * Answering AI questions updates the new-task corpus (pack + description).
 */
type CreateInterviewStep =
  | 'seed'
  | 'value'
  | 'outcome'
  | 'priority'
  | 'ai_questions'
  | 'dup_check'
  | 'placement'
  | 'score'
  | 'confirm';
/** Close-out interview: chat captures business value; optionally spawn follow-up tasks. */
type CloseInterviewStep = 'idle' | 'ask_value' | 'ask_spawn' | 'collect_spawns' | 'ready';
/** Nested interview to create a work epic (title + goal description). */
type EpicCreateStep = 'idle' | 'form' | 'ask_title' | 'ask_description';

export type TaskScoreModalIntent = 'review' | 'complete' | 'create';

interface TaskScoreModalProps {
  isOpen: boolean;
  /** null = board intake (dump → create task). */
  task: NetworkingTaskRow | null;
  onClose: () => void;
  onSaved: () => void;
  intent?: TaskScoreModalIntent;
  /** Optional email for create when no subscriber context. */
  defaultContactEmail?: string;
  /** Eval board epic filter — preselect on create so we skip guessing. */
  defaultEpicId?: string | null;
}

function emptyAxes(): TaskValueAxisScores {
  return { economic: 0, goals: 0, network: 0 };
}

function newMessageId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
}

function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultDueLocal(): string {
  const today =
    toDateOnly(new Date().toISOString()) || new Date().toISOString().slice(0, 10);
  return localDateAtHour(today, 9, 0);
}

/** Remaining dump after the title line → description seed. */
function descriptionFromDump(text: string): string {
  const lines = text.split('\n').map((l) => l.trim());
  const firstIdx = lines.findIndex((l) => l.length > 0);
  if (firstIdx < 0) return '';
  return lines
    .slice(firstIdx + 1)
    .join('\n')
    .trim()
    .slice(0, 4000);
}

/** Soft fallback only when the API fails — prefer OpenAI NAME/DESCRIPTION. */
function fallbackCreateName(seed: string): string {
  const line = seed
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l.length > 0);
  if (!line) return 'Untitled task';
  return line.length > 42 ? `${line.slice(0, 39).trim()}…` : line;
}

/** Soft fallback description from planning fields when API fails. */
function fallbackCreateDescription(
  seed: string,
  valueAnswer: string,
  outcomeAnswer: string,
  priorityAnswer: string,
): string {
  const lines = [
    valueAnswer.trim(),
    outcomeAnswer.trim() ? `Done: ${outcomeAnswer.trim()}` : '',
    priorityAnswer.trim() ? `If not: ${priorityAnswer.trim()}` : '',
  ].filter(Boolean);
  if (lines.length === 0) {
    const rest = descriptionFromDump(seed);
    return (rest || seed).slice(0, 280) || 'Planning summary pending.';
  }
  return lines.join('\n').slice(0, 520);
}

function activeEpicsForMenu(epics: WorkEpicRow[]): WorkEpicRow[] {
  return epics.filter((e) => e.status === 'active' || e.status === 'paused');
}

const EPIC_MATCH_STOPWORDS = new Set([
  'the',
  'and',
  'for',
  'with',
  'this',
  'that',
  'from',
  'have',
  'what',
  'when',
  'will',
  'into',
  'your',
  'about',
  'task',
  'done',
  'looks',
  'like',
  'need',
  'just',
  'dont',
  "don't",
  'does',
  'are',
  'was',
  'were',
  'been',
  'being',
  'our',
  'out',
  'get',
  'got',
  'can',
  'could',
  'should',
  'would',
  'make',
  'made',
  'more',
  'than',
  'then',
  'them',
  'they',
  'their',
  'there',
  'here',
  'also',
  'only',
  'over',
  'into',
  'onto',
  'after',
  'before',
  'because',
  'value',
  'priority',
  'standalone',
  'inbox',
  'ops',
  'plan',
  'work',
  'epic',
]);

function tokenizeForEpicMatch(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/[\s_-]+/)
    .map((w) => w.trim())
    .filter((w) => w.length >= 3 && !EPIC_MATCH_STOPWORDS.has(w));
}

function scoreEpicAgainstChat(epic: WorkEpicRow, corpus: string): number {
  const raw = corpus.toLowerCase();
  const tokens = new Set(tokenizeForEpicMatch(corpus));
  if (tokens.size === 0 && !raw.trim()) return 0;

  let score = 0;
  const title = epic.title.toLowerCase();
  const slug = epic.slug.toLowerCase();
  const desc = (epic.description ?? '').toLowerCase();

  if (title.length >= 3 && raw.includes(title)) score += 14;
  if (slug !== 'ops-inbox' && slug.length >= 3 && raw.includes(slug.replace(/-/g, ' '))) score += 10;
  if (slug !== 'ops-inbox' && raw.includes(slug)) score += 8;

  for (const t of tokenizeForEpicMatch(epic.title)) {
    if (tokens.has(t)) score += 5;
  }
  for (const t of tokenizeForEpicMatch(epic.slug.replace(/-/g, ' '))) {
    if (tokens.has(t)) score += 4;
  }
  for (const t of tokenizeForEpicMatch(epic.description ?? '')) {
    if (tokens.has(t)) score += 2;
  }

  // Light boost if description phrase fragments appear
  if (desc) {
    for (const chunk of desc.split(/[.,;:\n]/).map((c) => c.trim()).filter((c) => c.length >= 8)) {
      if (raw.includes(chunk.slice(0, 40))) score += 3;
    }
  }

  // Ops inbox is the fallback — never win on keyword noise
  if (epic.slug === 'ops-inbox') return Math.min(score, 1);

  return score;
}

type EpicRecommendation = {
  epic: WorkEpicRow;
  score: number;
  why: string;
};

function recommendEpicFromChat(
  corpus: string,
  epics: WorkEpicRow[],
): EpicRecommendation | null {
  const list = activeEpicsForMenu(epics).filter((e) => e.slug !== 'ops-inbox');
  if (list.length === 0) return null;

  const ranked = list
    .map((epic) => {
      const score = scoreEpicAgainstChat(epic, corpus);
      const corpusTokens = new Set(tokenizeForEpicMatch(corpus));
      const hits = [
        ...tokenizeForEpicMatch(epic.title),
        ...tokenizeForEpicMatch(epic.slug.replace(/-/g, ' ')),
      ].filter((t) => corpusTokens.has(t));
      const why =
        hits.length > 0
          ? `matches ${[...new Set(hits)].slice(0, 4).join(', ')}`
          : score >= 8
            ? 'title/description overlap in the chat'
            : 'weak overlap';
      return { epic, score, why };
    })
    .sort((a, b) => b.score - a.score);

  const best = ranked[0];
  if (!best || best.score < 4) return null;
  const second = ranked[1]?.score ?? 0;
  // Prefer a clear winner; still recommend if absolute score is strong
  if (best.score < 8 && best.score - second < 2) return null;
  return best;
}

function formatEpicMenu(epics: WorkEpicRow[]): string {
  const list = activeEpicsForMenu(epics);
  if (list.length === 0) return '(No active epics yet — choose standalone / Ops inbox.)';
  return list
    .map((e, i) => {
      const inbox = e.slug === 'ops-inbox' ? ' — catch-all inbox' : '';
      const target = e.target_date ? ` · deadline ${e.target_date}` : '';
      return `${i + 1}. ${e.title}${inbox}${target}`;
    })
    .join('\n');
}

function formatPlacementPrompt(
  epics: WorkEpicRow[],
  recommendation: EpicRecommendation | null,
): string {
  const menu = formatEpicMenu(epics);
  if (recommendation) {
    return (
      `Placement — recommendation from this chat:\n` +
      `→ «${recommendation.epic.title}» (${recommendation.why}).\n\n` +
      `Reply yes to take it, standalone / inbox, another number, or “new epic”.\n\n` +
      menu
    );
  }
  return (
    `Placement — no clear epic match from the chat yet.\n\n` +
    `Standalone (Ops inbox), pick a number, or say “new epic”.\n\n` +
    menu
  );
}

function matchEpicFromReply(text: string, epics: WorkEpicRow[]): WorkEpicRow | null {
  const list = activeEpicsForMenu(epics);
  const t = text.trim().toLowerCase();
  const num = t.match(/^(\d{1,2})\b/);
  if (num) {
    const idx = Number(num[1]) - 1;
    if (idx >= 0 && idx < list.length) return list[idx];
  }
  return (
    list.find(
      (e) =>
        t === e.slug.toLowerCase() ||
        t === e.title.toLowerCase() ||
        t.includes(e.title.toLowerCase()) ||
        t.includes(e.slug.toLowerCase()),
    ) ?? null
  );
}

function acceptsEpicRecommendation(text: string): boolean {
  const t = text.trim().toLowerCase();
  return (
    looksLikeYes(t) ||
    /^(yes|y|ok|okay|sure|that one|the rec|recommended|take it|go with (it|that)|sounds right)\b/.test(
      t,
    ) ||
    /\b(yes|take (the )?rec(ommendation)?|go with (that|it))\b/.test(t)
  );
}

function wantsStandalone(text: string): boolean {
  const t = text.trim().toLowerCase();
  return (
    /\b(standalone|stand[- ]alone|ops[-\s]?inbox|inbox only|no epic|not (an )?epic|neither)\b/.test(
      t,
    ) ||
    t === 'inbox' ||
    t === 'standalone' ||
    t === 'ops-inbox'
  );
}

/** Pull strategy_score 1–5 from chat (leading digit or “score 4”). */
function parseStrategyScore(text: string): number | null {
  const t = text.trim();
  const m =
    t.match(/^([1-5])(?:\s|\/|·|-|$)/) ||
    t.match(/\bscore\s*[:=]?\s*([1-5])\b/i) ||
    t.match(/\b([1-5])\s*\/\s*5\b/) ||
    t.match(/^([1-5])$/);
  if (!m) return null;
  const n = Number(m[1]);
  return n >= 1 && n <= 5 ? n : null;
}

const SCORE_RUBRIC =
  'Enter a number from 1 to 5 for the score (High value = 4–5):\n1 Low pull · 2 Weak · 3 Maybe · 4 Strong · 5 Must.\nType just the number (e.g. 4), then optionally a one-line why ($ / goals / network).';

function applyNameOrDescriptionCommand(
  text: string,
): { kind: 'name' | 'description'; value: string } | null {
  const nameMatch = text.match(/^(?:name|title|rename)\s*[:\-]\s*(.+)$/i);
  if (nameMatch?.[1]?.trim()) {
    return { kind: 'name', value: nameMatch[1].trim().slice(0, 56) };
  }
  const descMatch = text.match(/^(?:desc|description|summary|reword)\s*[:\-]\s*([\s\S]+)$/i);
  if (descMatch?.[1]?.trim()) {
    return { kind: 'description', value: descMatch[1].trim().slice(0, 520) };
  }
  return null;
}


function wantsNewEpic(text: string): boolean {
  return /\b(new epic|create epic|add epic|make an epic|another epic)\b/i.test(text.trim());
}

/** Trigger phrases are not epic titles — e.g. user said “new epic” to start create. */
function isEpicTriggerPhrase(text: string): boolean {
  const t = text.trim().toLowerCase();
  return (
    t === 'new epic' ||
    t === 'create epic' ||
    t === 'add epic' ||
    t === 'make an epic' ||
    t === 'another epic'
  );
}

function mentionsMoreWork(text: string): boolean {
  return /\b(more work|follow[- ]?ups?|still need|remaining|leftover|next steps?|also need|another task|create (a )?tasks?|not fully done|partial(ly)?|more to do|continue|after this)\b/i.test(
    text,
  );
}

function looksLikeYes(text: string): boolean {
  const t = text.trim().toLowerCase();
  return /^(y|yes|yeah|yep|sure|ok|okay|please|do it|go ahead)\b/.test(t) || /\byes\b/.test(t);
}

function looksLikeNo(text: string): boolean {
  const t = text.trim().toLowerCase();
  return /^(n|no|nope|nah)\b/.test(t) || /\b(no thanks|not now|skip|don'?t)\b/.test(t);
}

/** Count regex hits in corpus (capped). */
function countEvidenceHits(corpus: string, patterns: RegExp[]): number {
  let n = 0;
  for (const p of patterns) {
    const m = corpus.match(p);
    if (m) n += m.length;
  }
  return n;
}

function clamp03(n: number): number {
  return Math.max(0, Math.min(3, Math.round(n)));
}

/**
 * Force rescore — evaluate task fields + chat thread for $ / goals / network evidence,
 * roll a 1–5 score. Not a chat interview.
 */
function evaluateTaskFromEvidence(args: {
  title: string;
  notes: string;
  messages: TaskEvalMessage[];
  existing: TaskValueEvidence | null;
  epicTitle?: string | null;
  epicDescription?: string | null;
}): {
  score: number;
  axes: TaskValueAxisScores;
  summary: string;
  resources: TaskValueResource[];
} {
  const chatText = args.messages.map((m) => m.content).join('\n');
  const corpus = [
    args.title,
    args.notes,
    chatText,
    args.existing?.summary ?? '',
    args.epicTitle ?? '',
    args.epicDescription ?? '',
  ]
    .filter(Boolean)
    .join('\n')
    .toLowerCase();

  const economicRaw = countEvidenceHits(corpus, [
    /\b(revenue|paid|paying|cash|money|monetiz|invoice|client|retainer|pipeline|deal|close|pricing|\$|usd|mrr|arr)\b/gi,
  ]);
  const goalsRaw = countEvidenceHits(corpus, [
    /\b(goal|milestone|ship|launch|deadline|quarter|focus|plan|epic|deliver|outcome|done looks|priority|must)\b/gi,
  ]);
  const networkRaw = countEvidenceHits(corpus, [
    /\b(network|linkedin|intro|relationship|icp|audience|list|community|partner|referral|meet|call|outreach)\b/gi,
  ]);
  const urgency = countEvidenceHits(corpus, [
    /\b(urgent|blocker|blocking|critical|asap|today|this week|must)\b/gi,
  ]);
  const weak = countEvidenceHits(corpus, [
    /\b(maybe|someday|nice to have|low priority|optional|backlog|unclear)\b/gi,
  ]);

  // Existing axes as prior if present
  const prior = args.existing?.axes;
  let economic = clamp03((prior?.economic ?? 0) * 0.4 + Math.min(3, economicRaw * 0.9));
  let goals = clamp03((prior?.goals ?? 0) * 0.4 + Math.min(3, goalsRaw * 0.85));
  let network = clamp03((prior?.network ?? 0) * 0.4 + Math.min(3, networkRaw * 0.85));

  if (urgency > 0) {
    goals = clamp03(goals + 1);
    economic = clamp03(economic + (economicRaw > 0 ? 1 : 0));
  }
  if (weak > urgency) {
    economic = clamp03(economic - 1);
    goals = clamp03(goals - 1);
  }

  // Thin corpus → stay conservative / keep prior strategy score influence
  const substance =
    args.title.trim().length +
    args.notes.trim().length +
    chatText.trim().length +
    (args.existing?.summary?.length ?? 0);
  if (substance < 40 && args.existing?.strategy_score) {
    // Almost no evidence — keep prior score, note thin evidence
    const score = args.existing.strategy_score;
    return {
      score,
      axes: prior ?? emptyAxes(),
      summary: `Thin evidence in title/notes/chat — kept prior score ${score}/5. Add value signals ($ / goals / network) then Force rescore again.`,
      resources: [...(args.existing.resources ?? [])],
    };
  }

  const axes: TaskValueAxisScores = { economic, goals, network };
  let score = strategyScoreFromAxes(axes);
  if (urgency >= 2 && score < 4) score = Math.min(5, score + 1);
  if (weak >= 2 && score > 2) score = Math.max(1, score - 1);

  const resources: TaskValueResource[] = [...(args.existing?.resources ?? [])];
  for (const m of args.messages) {
    if (m.sources?.length) {
      for (const s of m.sources) {
        if (!resources.some((r) => r.label === s.label && r.id === s.id)) {
          resources.push(s);
        }
      }
    }
  }
  // Cite what we read
  const cite: TaskValueResource[] = [
    {
      kind: 'other',
      label: 'Task title + description',
      note: 'Primary fields scanned for value signals',
    },
  ];
  if (args.messages.length > 0) {
    cite.push({
      kind: 'other',
      label: `Chat thread (${args.messages.length} msgs)`,
      note: 'Eval messages scanned for $ / goals / network',
    });
  }
  if (args.epicTitle) {
    cite.push({
      kind: 'other',
      label: `Epic: ${args.epicTitle}`,
      note: 'Plan context',
    });
  }
  for (const c of cite) {
    if (!resources.some((r) => r.label === c.label)) resources.push(c);
  }

  const parts = [
    `Force rescore from evidence → ${score}/5 (${STRATEGY_SCORE_LABELS[score] ?? 'scored'}).`,
    `Axes: $ ${economic}/3 · goals ${goals}/3 · network ${network}/3.`,
  ];
  const signals: string[] = [];
  if (economicRaw) signals.push(`money×${economicRaw}`);
  if (goalsRaw) signals.push(`goals×${goalsRaw}`);
  if (networkRaw) signals.push(`network×${networkRaw}`);
  if (urgency) signals.push(`urgency×${urgency}`);
  if (signals.length) parts.push(`Signals: ${signals.join(', ')}.`);
  else parts.push('Few explicit value keywords — score leans on structure + prior.');

  return {
    score,
    axes,
    summary: parts.join(' ').slice(0, 800),
    resources: resources.slice(0, 20),
  };
}

function parseFollowUpLines(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.replace(/^[-*•\d.)\s]+/, '').trim())
    .filter((l) => l.length > 0)
    .slice(0, 12);
}

const TaskScoreModal: React.FC<TaskScoreModalProps> = ({
  isOpen,
  task,
  onClose,
  onSaved,
  intent = 'review',
  defaultContactEmail = 'stephan.smith.bc93@gmail.com',
  defaultEpicId = null,
}) => {
  const isCreate = task == null || intent === 'create';
  const existing = useMemo(
    () => (task ? readTaskValueEvidence(task.metadata) : null),
    [task],
  );

  const [uiTab, setUiTab] = useState<UiTab>('chat');
  const [closing, setClosing] = useState(false);
  const [createStep, setCreateStep] = useState<CreateInterviewStep>('seed');
  const [createPack, setCreatePack] = useState<CreatePack>(() => emptyCreatePack());
  const [createPriority, setCreatePriority] = useState('');
  const [similarTasks, setSimilarTasks] = useState<SimilarOpenTask[]>([]);
  const [dupCleared, setDupCleared] = useState(false);
  const [pendingAiQuestions, setPendingAiQuestions] = useState<string[]>([]);
  const [activeAiQuestion, setActiveAiQuestion] = useState<string | null>(null);
  const [closeStep, setCloseStep] = useState<CloseInterviewStep>('idle');
  const [epicCreateStep, setEpicCreateStep] = useState<EpicCreateStep>('idle');
  const [epicDraftTitle, setEpicDraftTitle] = useState('');
  const [epicDraftDescription, setEpicDraftDescription] = useState('');
  const [epicDraftDeadline, setEpicDraftDeadline] = useState('');
  const [epicSaving, setEpicSaving] = useState(false);
  const [recommendedEpicId, setRecommendedEpicId] = useState<string | null>(null);
  const [followUpSeeds, setFollowUpSeeds] = useState<string[]>([]);
  const [score, setScore] = useState<number | ''>('');
  const [axes, setAxes] = useState<TaskValueAxisScores>(emptyAxes);
  const [scoreWhy, setScoreWhy] = useState('');
  const [completionJustification, setCompletionJustification] = useState('');
  const [evalState, setEvalState] = useState<TaskEval>(emptyTaskEval);
  const [chatDraft, setChatDraft] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [dueDraft, setDueDraft] = useState('');
  const [scheduleHorizon, setScheduleHorizon] = useState<ScheduleHorizon | null>(null);
  const [dueWhy, setDueWhy] = useState('');
  const [epicId, setEpicId] = useState('');
  const [epicPlacement, setEpicPlacement] = useState<EpicPlacement>('suggested');
  const [epics, setEpics] = useState<WorkEpicRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [corpusLooking, setCorpusLooking] = useState(false);
  const createChatEndRef = useRef<HTMLDivElement | null>(null);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);

  const reloadEpics = useCallback(async (): Promise<WorkEpicRow[]> => {
    const { data } = await supabase
      .from('work_epics')
      .select('*')
      .eq('status', 'active')
      .order('sort_order', { ascending: true });
    const rows = (data ?? []) as WorkEpicRow[];
    setEpics(rows);
    return rows;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    void reloadEpics();
  }, [isOpen, reloadEpics]);

  useEffect(() => {
    if (!isOpen) return;
    if (task) {
      const ev = readTaskValueEvidence(task.metadata);
      const te = readTaskEval(task.metadata);
      setScore(
        task.strategy_score != null && task.strategy_score >= 1 && task.strategy_score <= 5
          ? task.strategy_score
          : ev?.strategy_score ?? '',
      );
      setAxes(ev?.axes ?? emptyAxes());
      setScoreWhy(ev?.summary ?? '');
      setCompletionJustification(
        te.completion_justification ?? ev?.completion_justification ?? '',
      );
      if (intent === 'complete') {
        const opener =
          'Close-out: what business value landed, and what was completed? ($ / goals / network / relationship — be concrete.)';
        const alreadyAsked = te.messages.some(
          (m) => m.role === 'peer' && m.content.includes('business value landed'),
        );
        setEvalState(
          alreadyAsked
            ? te
            : {
                ...te,
                messages: [
                  ...te.messages,
                  {
                    id: newMessageId(),
                    role: 'peer',
                    content: opener,
                    at: new Date().toISOString(),
                  },
                ],
                updated_at: new Date().toISOString(),
              },
        );
      } else {
        setEvalState(te);
      }
      setTitleDraft(task.title ?? '');
      setNotesDraft(task.notes ?? '');
      setDueDraft(task.due_at ? toDatetimeLocalValue(task.due_at) : defaultDueLocal());
      setEpicId(task.epic_id ?? '');
      setEpicPlacement(normalizeEpicPlacement(task.epic_placement) ?? 'suggested');
    } else {
      setScore('');
      setAxes(emptyAxes());
      setScoreWhy('');
      setCompletionJustification('');
      setEvalState(emptyTaskEval());
      setTitleDraft('');
      setNotesDraft('');
      // Leave due empty until AI schedules against the board timeline
      setDueDraft('');
      setEpicId(defaultEpicId && defaultEpicId !== 'all' ? defaultEpicId : '');
      setEpicPlacement(defaultEpicId && defaultEpicId !== 'all' ? 'tentative' : 'suggested');
      setScheduleHorizon(null);
      setDueWhy('');
    }
    setChatDraft('');
    setUiTab('chat');
    setClosing(intent === 'complete');
    setCreateStep('seed');
    setCreatePack(emptyCreatePack());
    setCreatePriority('');
    setSimilarTasks([]);
    setDupCleared(false);
    setPendingAiQuestions([]);
    setActiveAiQuestion(null);
    setCloseStep(intent === 'complete' ? 'ask_value' : 'idle');
    setEpicCreateStep('idle');
    setEpicDraftTitle('');
    setEpicDraftDescription('');
    setEpicDraftDeadline('');
    setEpicSaving(false);
    setRecommendedEpicId(null);
    setFollowUpSeeds([]);
  }, [isOpen, task, intent, defaultEpicId]);

  // Keep chat pinned to the latest message (create + edit).
  useEffect(() => {
    if (!isOpen) return;
    if (!isCreate && uiTab !== 'chat') return;
    const pin = () => {
      const el = chatScrollRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
      createChatEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    };
    pin();
    const t1 = window.setTimeout(pin, 40);
    const t2 = window.setTimeout(pin, 160);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isOpen, isCreate, uiTab, evalState.messages.length, createStep, closeStep]);

  // Epics load for planning interview — never auto-attach create to an epic.
  if (!isOpen) return null;

  const showHand = task
    ? isManualTaskSource(task.source) || existing?.scored_by === 'manual'
    : true;
  const currentScore = task?.strategy_score ?? (typeof score === 'number' ? score : null);

  const appendChat = (role: TaskEvalMessage['role'], content: string, next?: TaskEval) => {
    const text = content.trim();
    if (!text) return next ?? evalState;
    const base = next ?? evalState;
    const updated: TaskEval = {
      ...base,
      messages: [
        ...base.messages,
        { id: newMessageId(), role, content: text, at: new Date().toISOString() },
      ],
      updated_at: new Date().toISOString(),
    };
    setEvalState(updated);
    return updated;
  };

  const startCloseOut = (fromState?: TaskEval) => {
    setClosing(true);
    setEpicCreateStep('idle');
    setUiTab('chat');
    setCloseStep('ask_value');
    setFollowUpSeeds([]);
    const base = fromState ?? evalState;
    const alreadyAsked = base.messages.some(
      (m) => m.role === 'peer' && m.content.includes('business value landed'),
    );
    if (!alreadyAsked) {
      appendChat(
        'peer',
        'Close-out: what business value landed, and what was completed? ($ / goals / network / relationship — be concrete.)',
        base,
      );
    }
  };

  /** Chat path only — + button uses the left-panel form. */
  const startCreateEpicViaChat = (fromState?: TaskEval) => {
    setEpicCreateStep('ask_title');
    setEpicDraftTitle('');
    setEpicDraftDescription('');
    setEpicDraftDeadline('');
    setUiTab('chat');
    appendChat(
      'peer',
      'New epic — what’s the short name of this plan? (e.g. Sept Microconf — not “new epic”)',
      fromState ?? evalState,
    );
  };

  const openCreateEpicForm = () => {
    setEpicCreateStep('form');
    setEpicDraftTitle('');
    setEpicDraftDescription('');
    setEpicDraftDeadline('');
  };

  const cancelCreateEpicForm = () => {
    setEpicCreateStep('idle');
    setEpicDraftTitle('');
    setEpicDraftDescription('');
    setEpicDraftDeadline('');
  };

  const persistNewEpic = async (
    title: string,
    description: string,
    deadline?: string,
  ): Promise<WorkEpicRow | null> => {
    const titleTrim = title.trim();
    if (!titleTrim || isEpicTriggerPhrase(titleTrim)) {
      toast.error('Give the epic a real plan name (not “new epic”).');
      return null;
    }
    let slug = slugifyEpicTitle(titleTrim);
    const deadlineTrim = (deadline ?? epicDraftDeadline).trim();
    const payload = {
      slug,
      title: titleTrim,
      description: description.trim() || null,
      target_date: deadlineTrim || null,
      status: 'active' as const,
      sort_order: 50,
    };
    let { data, error } = await supabase.from('work_epics').insert(payload).select('*').single();
    if (error?.code === '23505') {
      slug = `${slug}-${Date.now().toString(36).slice(-4)}`;
      ({ data, error } = await supabase
        .from('work_epics')
        .insert({ ...payload, slug })
        .select('*')
        .single());
    }
    if (error) {
      reportUiError('tasks', 'create_work_epic', error);
      toast.error(error.message || 'Failed to create epic');
      return null;
    }
    const row = data as WorkEpicRow;
    await reloadEpics();
    setEpicId(row.id);
    setEpicPlacement('tentative');
    return row;
  };

  const submitCreateEpicForm = async () => {
    if (epicSaving) return;
    setEpicSaving(true);
    try {
      const row = await persistNewEpic(
        epicDraftTitle,
        epicDraftDescription,
        epicDraftDeadline,
      );
      if (!row) return;
      setEpicCreateStep('idle');
      setEpicDraftTitle('');
      setEpicDraftDescription('');
      setEpicDraftDeadline('');
      appendChat(
        'peer',
        `Created epic «${row.title}» and attached this task (tentative).`,
      );
      if (createStep === 'placement') {
        setCreateStep('score');
        appendChat('peer', SCORE_RUBRIC);
      }
      toast.success(`Epic «${row.title}» created`);
    } finally {
      setEpicSaving(false);
    }
  };

  /**
   * Force rescore — corpus lookup (same edge as MCP ask_corpus) then local axes eval + persist.
   * Not a chat interview.
   */
  const runForceRescore = async () => {
    if (!task || isCreate) return;
    if (saving || corpusLooking) return;
    setClosing(false);
    setSaving(true);
    setCorpusLooking(true);
    setUiTab('chat');
    try {
      const chosenEpic =
        epics.find((e) => e.id === (epicId || task.epic_id)) ?? task.epic ?? null;
      const title = titleDraft.trim() || task.title || '';
      const notes = notesDraft.trim() || task.notes || '';

      const looking = appendChat(
        'peer',
        'Evaluating on the server…',
      );

      const corpusHit = await lookupTaskCorpus({
        purpose: 'force_rescore',
        title,
        notes,
        epicTitle: chosenEpic?.title ?? null,
      });

      let messagesForEval = looking.messages;
      if (corpusHit?.digest) {
        // Keep evidence in task notes path, not a JSON dump in chat
        const withNote = appendChat(
          'system',
          corpusHit.digest
            .split('\n')
            .map((l) => l.replace(/^[-*]\s*/, ''))
            .filter(Boolean)
            .slice(0, 4)
            .map((l) => `· ${l}`)
            .join('\n') || 'Evidence reviewed.',
          looking,
        );
        messagesForEval = withNote.messages;
      }

      const prior = existing ?? readTaskValueEvidence(task.metadata);
      const result = evaluateTaskFromEvidence({
        title,
        notes: corpusHit?.digest
          ? `${notes}\n\n${corpusHit.digest}`.slice(0, 4000)
          : notes,
        messages: messagesForEval,
        existing: prior,
        epicTitle: chosenEpic?.title ?? null,
        epicDescription: chosenEpic?.description ?? null,
      });

      const resources = [
        ...result.resources,
        ...(corpusHit?.resources ?? []),
      ].filter(
        (r, i, arr) => arr.findIndex((x) => x.label === r.label && x.id === r.id) === i,
      );

      const summary = corpusHit?.digest
        ? `${result.summary}\n\n${corpusHit.digest.slice(0, 400)}`.slice(0, 800)
        : result.summary;

      setScore(result.score);
      setAxes(result.axes);
      setScoreWhy(summary);

      const note = `Force rescore recorded ${result.score}/5. ${summary}`;
      const nextEval: TaskEval = {
        ...evalState,
        messages: [
          ...messagesForEval,
          {
            id: newMessageId(),
            role: 'system',
            content: note,
            at: new Date().toISOString(),
            sources: resources.slice(0, 8),
          },
        ],
        updated_at: new Date().toISOString(),
      };
      // Re-sync from messagesForEval base — evalState may be stale vs looking
      setEvalState(nextEval);

      const evidence: TaskValueEvidence = {
        version: 1,
        axes: result.axes,
        strategy_score: result.score,
        summary,
        resources: resources.slice(0, 20),
        scored_at: new Date().toISOString(),
        scored_by: 'agent',
        work_justification: prior?.work_justification,
        completion_justification:
          completionJustification.trim() || prior?.completion_justification,
      };

      let metadata = withTaskEval(task.metadata, nextEval);
      metadata = withTaskValueEvidence(metadata, evidence);

      const { error } = await supabase
        .from('tasks')
        .update({
          strategy_score: result.score,
          metadata,
          updated_at: new Date().toISOString(),
        })
        .eq('id', task.id);
      if (error) throw error;

      toast.success(
        corpusHit
          ? `Rescored ${result.score}/5 with corpus`
          : `Rescored ${result.score}/5 from task evidence`,
      );
      onSaved();
    } catch (e: unknown) {
      reportUiError('tasks', 'force_rescore', e, { task_id: task.id });
      toast.error(e instanceof Error ? e.message : 'Force rescore failed');
    } finally {
      setSaving(false);
      setCorpusLooking(false);
    }
  };

  /** Load open tasks + epic target for AI scheduling (occupancy); model proposes, we clamp. */
  const loadScheduleBoardContext = async (
    forEpicId?: string | null,
  ): Promise<ScheduleBoardContext> => {
    const todayIso = toDateOnly(new Date().toISOString()) || new Date().toISOString().slice(0, 10);
    const epic = epics.find((e) => e.id === (forEpicId || epicId)) ?? null;

    const { data, error } = await supabase
      .from('tasks')
      .select('id, title, strategy_score, due_at, epic_id')
      .in('status', ['pending', 'in_progress'])
      .order('due_at', { ascending: true })
      .limit(60);
    if (error) {
      reportUiError('tasks', 'schedule_board_context', error);
    }

    return {
      todayIso,
      epicTitle: epic?.title ?? null,
      epicTargetDate: toDateOnly(epic?.target_date ?? null),
      openTasks: (data ?? []).map((t) => ({
        id: t.id as string,
        title: (t.title as string | null) ?? null,
        strategy_score: (t.strategy_score as number | null) ?? null,
        due_at: t.due_at as string,
        epic_id: (t.epic_id as string | null) ?? null,
      })),
    };
  };

  /**
   * Apply OpenAI ticket review: name, description, priority/failure, due, score, horizon.
   * Soft local due fallback only when the API returns nothing.
   */
  const applyApiReview = async (
    pack: CreatePack,
    opts?: {
      existing?: CreateTicketReview;
      newInfo?: string;
      /** If true, always call review even when existing has name+description. */
      forceReview?: boolean;
      epicIdOverride?: string | null;
    },
  ): Promise<{
    pack: CreatePack;
    name: string;
    description: string;
    score: number | null;
    dueLocal: string;
    horizon: ScheduleHorizon | null;
    dueWhy: string;
  }> => {
    let review: CreateTicketReview | null = opts?.existing ?? null;
    const haveWording = Boolean(review?.name?.trim() && review?.description?.trim());
    const needFullReview =
      opts?.forceReview ||
      opts?.newInfo ||
      !haveWording ||
      review?.score == null ||
      !review?.dueLocal;

    const scheduleContext = await loadScheduleBoardContext(opts?.epicIdOverride ?? epicId);

    if (needFullReview) {
      setCorpusLooking(true);
      try {
        const next = await reviewCreateTicketViaCorpus({
          pack,
          name: review?.name || titleDraft || undefined,
          description: review?.description || notesDraft || undefined,
          score:
            review?.score ??
            (typeof score === 'number' ? score : null),
          dueLocal: dueDraft || undefined,
          newInfo: opts?.newInfo,
          scheduleContext,
        });
        if (next) {
          review = { ...review, ...next };
        }
      } finally {
        setCorpusLooking(false);
      }
    }

    const nextPack: CreatePack = {
      seed: review?.name || review?.seed || pack.seed,
      value: review?.value || pack.value,
      outcome: review?.outcome || pack.outcome,
      priority: review?.priority || pack.priority,
    };
    setCreatePack(nextPack);
    if (nextPack.priority) setCreatePriority(nextPack.priority);

    let name = (review?.name || '').trim();
    let description = (review?.description || '').trim();
    if (!name) name = titleDraft.trim() || fallbackCreateName(nextPack.seed);
    if (!description) {
      description =
        notesDraft.trim() ||
        fallbackCreateDescription(
          nextPack.seed,
          nextPack.value,
          nextPack.outcome,
          nextPack.priority || createPriority,
        );
    }
    setTitleDraft(name.slice(0, 56));
    setNotesDraft(description.slice(0, 520));

    let nextScore: number | null =
      review?.score != null && review.score >= 1 && review.score <= 5 ? review.score : null;
    if (nextScore != null) {
      setScore(nextScore);
      if (review?.scoreWhy) setScoreWhy(review.scoreWhy);
    } else if (typeof score === 'number') {
      nextScore = score;
    }

    if (review?.horizon) setScheduleHorizon(review.horizon);
    if (review?.dueWhy) setDueWhy(review.dueWhy);

    // AI may propose; we always clamp — never past; default today when missing/past
    const nextDue = resolveCreateDue(
      scheduleContext,
      review?.dueLocal?.trim() || dueDraft.trim() || null,
      review?.horizon ?? scheduleHorizon,
    );
    setDueDraft(nextDue);

    return {
      pack: nextPack,
      name: name.slice(0, 56),
      description: description.slice(0, 520),
      score: nextScore,
      dueLocal: nextDue,
      horizon: review?.horizon ?? scheduleHorizon,
      dueWhy: review?.dueWhy || dueWhy,
    };
  };

  const proposeWordingAfterPlacement = async (
    placementLabel: string,
    next: TaskEval,
    packOverride?: CreatePack,
    epicIdOverride?: string | null,
  ) => {
    const pack = packOverride ?? createPack;
    const applied = await applyApiReview(pack, {
      forceReview: true,
      epicIdOverride: epicIdOverride ?? epicId,
    });
    const scored =
      applied.score != null ||
      (typeof score === 'number' && score >= 1 && score <= 5);
    const scoreNum = applied.score ?? (typeof score === 'number' ? score : null);
    const dueLine = applied.dueLocal
      ? `\nDue: ${applied.dueLocal.slice(0, 10)}${applied.horizon ? ` (${applied.horizon}-term)` : ''}${applied.dueWhy ? ` — ${applied.dueWhy}` : ''}`
      : '';
    if (scored && scoreNum != null) {
      appendChat(
        'peer',
        `${placementLabel}\n\nCard name: ${applied.name}\nScore ${scoreNum}/5` +
          (scoreWhy ? ` — ${scoreWhy}` : '') +
          dueLine +
          `\n\nConfirm on the left, enrich if needed, then Create.`,
        next,
      );
      setCreateStep('confirm');
      return;
    }
    appendChat(
      'peer',
      `${placementLabel}\n\nCard name: ${applied.name}${dueLine}\n\n${SCORE_RUBRIC}`,
      next,
    );
    setCreateStep('score');
  };

  const fetchSimilarOpenTasks = async (corpus: string): Promise<SimilarOpenTask[]> => {
    const { data, error } = await supabase
      .from('tasks')
      .select('id, title, notes, status, strategy_score, epic_id')
      .in('status', ['pending', 'in_progress'])
      .order('updated_at', { ascending: false })
      .limit(100);
    if (error) {
      reportUiError('tasks', 'similar_open_tasks', error);
      return [];
    }
    return pickSimilarTasks(corpus, (data ?? []) as SimilarOpenTask[]);
  };

  /** After pack is filled enough: corpus → AI questions → dup → epic → score. */
  const continueAfterPack = async (
    pack: CreatePack,
    next: TaskEval,
    opts?: {
      forceCreate?: boolean;
      clarifyNote?: string;
      score?: number | null;
      scoreWhy?: string;
      wantsStandalone?: boolean;
      epicHint?: string;
      skipDup?: boolean;
      skipAiQuestions?: boolean;
      skipCorpus?: boolean;
    },
  ) => {
    setCreatePriority(pack.priority);
    const corpus = [pack.seed, pack.value, pack.outcome, pack.priority, titleDraft, notesDraft]
      .filter(Boolean)
      .join('\n');

    if (opts?.score != null && opts.score >= 1 && opts.score <= 5) {
      setScore(opts.score);
      if (opts.scoreWhy) setScoreWhy(opts.scoreWhy);
    }

    let corpusHit: Awaited<ReturnType<typeof lookupTaskCorpus>> = null;
    if (!opts?.skipCorpus) {
      setCorpusLooking(true);
      try {
        corpusHit = await lookupTaskCorpus({
          purpose: 'create_intake',
          seed: pack.seed,
          value: pack.value,
          outcome: pack.outcome,
          priority: pack.priority,
          title: titleDraft || undefined,
          notes: notesDraft || undefined,
        });
      } finally {
        setCorpusLooking(false);
      }

      if (corpusHit) {
        // OpenAI owns finished ticket fields (name, description, score, due, priority)
        await applyApiReview(pack, {
          existing: {
            name: corpusHit.name,
            description: corpusHit.description,
            score: corpusHit.score,
            scoreWhy: corpusHit.scoreWhy,
            dueLocal: corpusHit.dueLocal,
            priority: corpusHit.priority,
          },
        });

        if (!opts?.skipAiQuestions && corpusHit.questions.length > 0) {
          const [first, ...rest] = corpusHit.questions;
          setActiveAiQuestion(first);
          setPendingAiQuestions(rest);
          const list = formatOpenQuestionsList(corpusHit.questions);
          appendChat(
            'peer',
            `${list}\n\nReply to #1 below (or say skip).`,
            next,
          );
          setCreateStep('ai_questions');
          return;
        }
      }
    }

    const matchCorpus = corpusHit
      ? `${corpus}\n${corpusHit.digest}`
      : corpus;

    // Epic: board filter wins first, then hint / corpus match.
    let attachedEpic: WorkEpicRow | null = null;
    const inbox = epics.find((e) => e.slug === 'ops-inbox') ?? null;
    const boardPrefill =
      defaultEpicId && defaultEpicId !== 'all'
        ? epics.find((e) => e.id === defaultEpicId) ?? null
        : null;
    if (boardPrefill && !opts?.wantsStandalone) {
      attachedEpic = boardPrefill;
    } else if (opts?.wantsStandalone && inbox) {
      attachedEpic = inbox;
    } else if (opts?.epicHint) {
      attachedEpic = resolveEpicFromHint(opts.epicHint, epics);
    }
    if (!attachedEpic) {
      const recommendation = recommendEpicFromChat(matchCorpus, epics);
      if (recommendation) {
        setRecommendedEpicId(recommendation.epic.id);
        if (recommendation.score >= 6) {
          attachedEpic = recommendation.epic;
        }
      }
    } else {
      setRecommendedEpicId(attachedEpic.id);
    }
    if (attachedEpic) {
      setEpicId(attachedEpic.id);
      setEpicPlacement(
        boardPrefill && attachedEpic.id === boardPrefill.id
          ? 'tentative'
          : attachedEpic.slug === 'ops-inbox'
            ? 'suggested'
            : 'tentative',
      );
    }

    const cleared = dupCleared || opts?.skipDup || opts?.forceCreate || Boolean(opts?.clarifyNote);
    if (!cleared) {
      const similar = await fetchSimilarOpenTasks(matchCorpus);
      setSimilarTasks(similar);
      if (similar.length > 0) {
        const extras = [
          formatSimilarTasksPushback(similar),
          attachedEpic
            ? boardPrefill && attachedEpic.id === boardPrefill.id
              ? `Epic from board filter\n· ${attachedEpic.title}\n(already selected — change in the dropdown if wrong)`
              : `Likely epic\n· ${attachedEpic.title}\n(say yes to confirm, or pick another)`
            : '',
        ].filter(Boolean);
        appendChat('peer', formatGotSummary(pack, extras), next);
        setCreateStep('dup_check');
        return;
      }
      setDupCleared(true);
    } else if (opts?.clarifyNote) {
      next = appendChat('system', `Clarify vs existing work: ${opts.clarifyNote}`, next);
      setDupCleared(true);
    } else if (opts?.forceCreate) {
      next = appendChat('system', 'Forced create despite similar open tasks.', next);
      setDupCleared(true);
    }

    if (attachedEpic) {
      const fromBoard = Boolean(boardPrefill && attachedEpic.id === boardPrefill.id);
      await proposeWordingAfterPlacement(
        fromBoard
          ? `Using board epic «${attachedEpic.title}».`
          : `Matched epic «${attachedEpic.title}» (${attachedEpic.slug === 'ops-inbox' ? 'suggested' : 'tentative'}).`,
        next,
        pack,
        attachedEpic.id,
      );
      return;
    }

    const recommendation = recommendEpicFromChat(matchCorpus, epics);
    setRecommendedEpicId(recommendation?.epic.id ?? null);
    appendChat(
      'peer',
      `${formatGotSummary(pack)}\n\n${formatPlacementPrompt(epics, recommendation)}`,
      next,
    );
    setCreateStep('placement');
  };

  /** Fold an AI-clarifier answer into the pack, then refresh name/description via API. */
  const applyAiQuestionAnswer = async (
    question: string,
    answer: string,
    pack: CreatePack,
  ): Promise<CreatePack> => {
    const a = answer.trim().slice(0, 400);
    let nextPack = { ...pack };
    if (/\b(value|why|worth|money|revenue|goal|network)\b/i.test(question)) {
      nextPack = {
        ...nextPack,
        value: nextPack.value ? `${nextPack.value}; ${a}`.slice(0, 2000) : a,
      };
    } else if (/\b(done|outcome|complete|access|when)\b/i.test(question)) {
      nextPack = {
        ...nextPack,
        outcome: nextPack.outcome ? `${nextPack.outcome}; ${a}`.slice(0, 2000) : a,
      };
    } else if (/\b(priority|if we don|urgent|block)\b/i.test(question)) {
      nextPack = {
        ...nextPack,
        priority: nextPack.priority ? `${nextPack.priority}; ${a}`.slice(0, 2000) : a,
      };
      setCreatePriority(nextPack.priority);
    } else {
      nextPack = {
        ...nextPack,
        value: nextPack.value ? `${nextPack.value}; ${a}`.slice(0, 2000) : a,
      };
    }
    setCreatePack(nextPack);
    const applied = await applyApiReview(nextPack, { newInfo: answer, forceReview: true });
    return applied.pack;
  };

  const absorbCreateDump = async (text: string, next: TaskEval, basePack: CreatePack) => {
    const parsed = parseCreateDump(text);
    let pack = mergeCreatePack(basePack, parsed);
    let epicHint = parsed.epicHint;
    let inferredScore = parsed.score;
    let inferredScoreWhy = parsed.scoreWhy;

    // Step answers (short / unlabeled): fill the field we’re on without wiping prior seed.
    if (createStep === 'seed') {
      pack = { ...pack, seed: parsed.isRich ? pack.seed || text : text };
    } else if (createStep === 'value' && !parsed.value) {
      pack = { ...pack, value: text, seed: basePack.seed || pack.seed };
    } else if (createStep === 'outcome' && !parsed.outcome) {
      pack = { ...pack, outcome: text, seed: basePack.seed || pack.seed };
    } else if (createStep === 'priority' && !parsed.priority) {
      pack = { ...pack, priority: text, seed: basePack.seed || pack.seed };
    }

    // Every meaningful dump → AI reviews/tweaks the whole ticket (name, due, priority, score).
    let missing = missingCreateFields(pack);
    if (text.trim().length >= 40 && (parsed.isRich || createStep === 'seed' || missing.length > 0)) {
      setCorpusLooking(true);
      try {
        const scheduleContext = await loadScheduleBoardContext(epicId);
        const inferred = await inferCreatePackViaCorpus(
          [
            titleDraft && `Current name: ${titleDraft}`,
            notesDraft && `Current description:\n${notesDraft}`,
            pack.seed && `Work: ${pack.seed}`,
            pack.value && `Value: ${pack.value}`,
            pack.outcome && `Done: ${pack.outcome}`,
            pack.priority && `If not: ${pack.priority}`,
            `New dump:\n${text}`,
          ]
            .filter(Boolean)
            .join('\n\n'),
          scheduleContext,
        );
        if (inferred) {
          pack = {
            seed: inferred.name || inferred.seed || pack.seed || '',
            value: inferred.value || pack.value || '',
            outcome: inferred.outcome || pack.outcome || '',
            priority: inferred.priority || pack.priority || '',
          };
          const applied = await applyApiReview(pack, {
            existing: inferred,
          });
          pack = applied.pack;
          if (applied.score != null) inferredScore = applied.score;
          if (inferred.scoreWhy) inferredScoreWhy = inferred.scoreWhy;
          if (!epicHint && inferred.epicHint) epicHint = inferred.epicHint;
        } else {
          await applyApiReview(pack, { newInfo: text, forceReview: true });
        }
      } finally {
        setCorpusLooking(false);
      }
      missing = missingCreateFields(pack);
    }

    setCreatePack(pack);
    setCreatePriority(pack.priority);

    if (missing.length > 0) {
      const step = nextMissingCreateStep(pack);
      if (step === 'placement') {
        await continueAfterPack(pack, next, {
          forceCreate: parsed.forceCreate,
          clarifyNote: parsed.clarifyNote,
          score: inferredScore,
          scoreWhy: inferredScoreWhy,
          wantsStandalone: parsed.wantsStandalone,
          epicHint,
        });
        return;
      }
      setCreateStep(step);
      appendChat('peer', `${formatGotSummary(pack)}\n\n${askPromptForStep(step)}`, next);
      return;
    }

    await continueAfterPack(pack, next, {
      forceCreate: parsed.forceCreate,
      clarifyNote: parsed.clarifyNote,
      score: inferredScore,
      scoreWhy: inferredScoreWhy,
      wantsStandalone: parsed.wantsStandalone,
      epicHint,
    });
  };

  const handleSendChat = () => {
    const raw = chatDraft.trim();
    if (!raw) return;
    const text = cleanupUserPrompt(raw);
    let next = appendChat('user', text);
    setChatDraft('');

    if (isCreate) {
      // Nested interview: create a work epic (title + description), then resume placement.
      if (epicCreateStep === 'ask_title') {
        const title = text.trim().slice(0, 120);
        if (title.length < 2 || isEpicTriggerPhrase(title)) {
          appendChat(
            'peer',
            'Need a real plan name — e.g. Sept Microconf (not “new epic”).',
            next,
          );
          return;
        }
        setEpicDraftTitle(title);
        appendChat(
          'peer',
          `Epic name «${title}». What’s the description — what does this plan cover, and what does done look like for the epic?`,
          next,
        );
        setEpicCreateStep('ask_description');
        return;
      }
      if (epicCreateStep === 'ask_description') {
        const title = epicDraftTitle.trim() || text.trim().slice(0, 120);
        void (async () => {
          const row = await persistNewEpic(title, text);
          if (!row) {
            appendChat(
              'peer',
              'Couldn’t create that epic. Try again with a different name, or pick an existing epic.',
              next,
            );
            setEpicCreateStep('idle');
            return;
          }
          setEpicCreateStep('idle');
          const after = appendChat(
            'peer',
            `Created epic «${row.title}» and attached this task (tentative).`,
            next,
          );
          if (createStep === 'placement' || createStep === 'dup_check') {
            void proposeWordingAfterPlacement(
              `On new epic «${row.title}».`,
              after,
              createPack,
              row.id,
            );
          } else if (createStep === 'score' || createStep === 'confirm') {
            appendChat(
              'peer',
              `Epic updated. Continue with score (1–5) or Create when ready.\n\n${SCORE_RUBRIC}`,
              after,
            );
            setCreateStep('score');
          }
        })();
        return;
      }

      if (wantsNewEpic(text)) {
        startCreateEpicViaChat(next);
        return;
      }

      const inbox = epics.find((e) => e.slug === 'ops-inbox');

      if (createStep === 'ai_questions') {
        if (/^(skip|none|n\/a|no more)\b/i.test(text.trim())) {
          setPendingAiQuestions([]);
          setActiveAiQuestion(null);
          void continueAfterPack(createPack, next, {
            skipAiQuestions: true,
            skipCorpus: true,
          });
          return;
        }
        const q = activeAiQuestion;
        if (!q) {
          void continueAfterPack(createPack, next, {
            skipAiQuestions: true,
            skipCorpus: true,
          });
          return;
        }
        void (async () => {
          const updatedPack = await applyAiQuestionAnswer(q, text, createPack);
          const remaining = pendingAiQuestions;
          if (remaining.length > 0) {
            const [first, ...rest] = remaining;
            setActiveAiQuestion(first);
            setPendingAiQuestions(rest);
            appendChat(
              'peer',
              `Logged.\n\nNext:\n${first}\n\n(Or say skip.)`,
              next,
            );
            return;
          }
          setActiveAiQuestion(null);
          setPendingAiQuestions([]);
          appendChat('peer', 'Logged. Continuing…', next);
          await continueAfterPack(updatedPack, next, {
            skipAiQuestions: true,
            skipCorpus: true,
          });
        })();
        return;
      }

      if (createStep === 'dup_check') {
        if (acceptsForceCreate(text)) {
          setDupCleared(true);
          void continueAfterPack(createPack, next, { forceCreate: true, skipDup: true });
          return;
        }
        const clarify = extractClarifyNote(text);
        if (clarify) {
          setDupCleared(true);
          void continueAfterPack(createPack, next, { clarifyNote: clarify, skipDup: true });
          return;
        }
        appendChat(
          'peer',
          `Still blocked on similar work.\n\n${formatSimilarTasksPushback(similarTasks)}`,
          next,
        );
        return;
      }

      // Smart intake: absorb multi-answer dumps at early steps; only ask what's missing.
      if (
        createStep === 'seed' ||
        createStep === 'value' ||
        createStep === 'outcome' ||
        createStep === 'priority'
      ) {
        void absorbCreateDump(text, next, createPack);
        return;
      }

      if (createStep === 'placement') {
        if (wantsStandalone(text)) {
          if (!inbox) {
            appendChat(
              'peer',
              'No Ops inbox epic found. Say which epic number from the list, or we can’t place this yet.',
              next,
            );
            return;
          }
          setEpicId(inbox.id);
          setEpicPlacement('suggested');
          setRecommendedEpicId(null);
          void proposeWordingAfterPlacement(
            'Placed as standalone on Ops inbox (suggested).',
            next,
            createPack,
            inbox.id,
          );
          return;
        }

        if (acceptsEpicRecommendation(text) && recommendedEpicId) {
          const rec = epics.find((e) => e.id === recommendedEpicId);
          if (rec) {
            setEpicId(rec.id);
            setEpicPlacement(rec.slug === 'ops-inbox' ? 'suggested' : 'tentative');
            setRecommendedEpicId(null);
            void proposeWordingAfterPlacement(
              `Placed on recommended epic «${rec.title}» (tentative).`,
              next,
              createPack,
              rec.id,
            );
            return;
          }
        }

        const matched = matchEpicFromReply(text, epics);
        if (!matched) {
          const corpus = [
            createPack.seed,
            createPack.value,
            createPack.outcome,
            createPack.priority,
            titleDraft,
            notesDraft,
          ].join('\n');
          const recommendation = recommendEpicFromChat(corpus, epics);
          setRecommendedEpicId(recommendation?.epic.id ?? null);
          appendChat(
            'peer',
            `Couldn’t match that.\n\n${formatPlacementPrompt(epics, recommendation)}`,
            next,
          );
          return;
        }
        setEpicId(matched.id);
        setEpicPlacement(matched.slug === 'ops-inbox' ? 'suggested' : 'tentative');
        setRecommendedEpicId(null);
        void proposeWordingAfterPlacement(
          `Placed on epic «${matched.title}» (${matched.slug === 'ops-inbox' ? 'suggested' : 'tentative'}).`,
          next,
          createPack,
          matched.id,
        );
        return;
      }
      if (createStep === 'score') {
        const parsed = parseStrategyScore(text);
        if (parsed == null) {
          appendChat(
            'peer',
            `I still need a score. Enter a number from 1 to 5 (nothing else required on the first line).\n\n${SCORE_RUBRIC}`,
            next,
          );
          return;
        }
        setScore(parsed);
        const why = text.replace(/^.*?([1-5])\b/, '').replace(/^[\s:·\-–—]+/, '').trim();
        if (why) setScoreWhy(why);
        void (async () => {
          const { name } = await applyApiReview(createPack, {
            existing: {
              name: titleDraft.trim() || undefined,
              description: notesDraft.trim() || undefined,
              score: parsed,
              scoreWhy: why || undefined,
            },
            forceReview: true,
          });
          appendChat(
            'peer',
            `Score ${parsed}/5 locked for create.\n\nCard name: ${name}\n\nEnrich in chat (name: … / description: …) or Create.`,
            next,
          );
          setCreateStep('confirm');
        })();
        return;
      }

      // confirm+: enrichment + re-open placement/score
      if (/^(standalone|inbox|ops-inbox|\d+)\b/i.test(text.trim()) || matchEpicFromReply(text, epics)) {
        const corpus = [
          createPack.seed,
          createPack.value,
          createPack.outcome,
          createPack.priority,
          titleDraft,
          notesDraft,
        ].join('\n');
        const recommendation = recommendEpicFromChat(corpus, epics);
        setRecommendedEpicId(recommendation?.epic.id ?? null);
        appendChat(
          'peer',
          `Re-checking placement.\n\n${formatPlacementPrompt(epics, recommendation)}`,
          next,
        );
        setCreateStep('placement');
        setEpicId('');
        setScore('');
        return;
      }
      if (/^rescore\b/i.test(text.trim()) || parseStrategyScore(text) != null) {
        if (parseStrategyScore(text) != null) {
          const parsed = parseStrategyScore(text)!;
          setScore(parsed);
          const why = text.replace(/^.*?([1-5])\b/, '').replace(/^[\s:·\-–—]+/, '').trim();
          if (why) setScoreWhy(why);
          appendChat('peer', `Score updated to ${parsed}/5. Create when name + summary look right.`, next);
          return;
        }
        appendChat('peer', SCORE_RUBRIC, next);
        setCreateStep('score');
        return;
      }
      const wording = applyNameOrDescriptionCommand(text);
      if (wording?.kind === 'name') {
        setTitleDraft(wording.value);
        appendChat('peer', `Card name → «${wording.value}».`, next);
        return;
      }
      if (wording?.kind === 'description') {
        setNotesDraft(wording.value);
        appendChat('peer', 'Description updated (kept tight).', next);
        return;
      }
      // Enrichment — short → name override; longer → full AI ticket review
      if (text.length < 80 && !text.includes('\n')) {
        const name = text.trim().slice(0, 56);
        setTitleDraft(name);
        void (async () => {
          const applied = await applyApiReview(
            { ...createPack, seed: name },
            { newInfo: `Renamed to: ${name}`, forceReview: true },
          );
          appendChat(
            'peer',
            `Card name → «${applied.name}»` +
              (applied.score != null ? ` · score ${applied.score}/5` : '') +
              `. Create when ready.`,
            next,
          );
        })();
      } else {
        const nextPack = {
          ...createPack,
          value: createPack.value
            ? `${createPack.value}\n${text}`.slice(0, 2000)
            : text.slice(0, 2000),
        };
        setCreatePack(nextPack);
        void (async () => {
          const applied = await applyApiReview(nextPack, {
            newInfo: text,
            forceReview: true,
          });
          appendChat(
            'peer',
            `Reviewed ticket` +
              (applied.score != null ? ` · score ${applied.score}/5` : '') +
              `. Name + description + due/priority updated.`,
            next,
          );
        })();
      }
      return;
    }

    // Close-out interview — chat owns business value capture.
    if (closing || intent === 'complete') {
      if (closeStep === 'ask_value' || closeStep === 'idle') {
        setCompletionJustification(text);
        if (mentionsMoreWork(text)) {
          appendChat(
            'peer',
            'Stored that as business value. Sounds like there may be more work — should I create new task(s) for what’s left?',
            next,
          );
          setCloseStep('ask_spawn');
        } else {
          appendChat(
            'peer',
            'Stored that as business value on this task. Save value & close when you’re ready — or say if there’s remaining work.',
            next,
          );
          setCloseStep('ready');
        }
        return;
      }
      if (closeStep === 'ask_spawn') {
        if (looksLikeNo(text)) {
          appendChat('peer', 'No new tasks. Save value & close when ready.', next);
          setCloseStep('ready');
          return;
        }
        if (looksLikeYes(text)) {
          appendChat(
            'peer',
            'List each follow-up briefly — one per line. I’ll create them on Ops inbox after you close this task.',
            next,
          );
          setCloseStep('collect_spawns');
          return;
        }
        // Ambiguous — treat as value refinement or re-ask
        if (mentionsMoreWork(text)) {
          appendChat(
            'peer',
            'Should I create new task(s) for that remaining work? Yes or no.',
            next,
          );
          return;
        }
        setCompletionJustification((prev) => (prev ? `${prev}\n\n${text}` : text));
        appendChat(
          'peer',
          'Added that to business value. Create follow-up tasks for remaining work? Yes or no.',
          next,
        );
        return;
      }
      if (closeStep === 'collect_spawns') {
        const lines = parseFollowUpLines(text);
        if (lines.length === 0) {
          appendChat('peer', 'Need at least one follow-up line — or say “skip” to close without new tasks.', next);
          return;
        }
        if (looksLikeNo(text) || /^skip$/i.test(text.trim())) {
          setFollowUpSeeds([]);
          appendChat('peer', 'Skipping follow-ups. Save value & close when ready.', next);
          setCloseStep('ready');
          return;
        }
        setFollowUpSeeds(lines);
        appendChat(
          'peer',
          `Got ${lines.length} follow-up${lines.length === 1 ? '' : 's'}. Save value & close — I’ll create them on Ops inbox.`,
          next,
        );
        setCloseStep('ready');
        return;
      }
      if (closeStep === 'ready') {
        if (mentionsMoreWork(text) && followUpSeeds.length === 0) {
          appendChat(
            'peer',
            'Should I create new task(s) for that remaining work? Yes or no.',
            next,
          );
          setCloseStep('ask_spawn');
          return;
        }
        setCompletionJustification((prev) => (prev ? `${prev}\n\n${text}` : text));
        appendChat('peer', 'Updated business value. Save value & close when ready.', next);
        return;
      }
    }

    // Review chat — wording; Force rescore is an evidence eval (button or “rescore”), not Q&A.
    const wording = applyNameOrDescriptionCommand(text);
    if (wording?.kind === 'name') {
      setTitleDraft(wording.value);
      appendChat(
        'peer',
        `Card name → «${wording.value}». Save to persist. Say description: … to reword, or Force rescore to re-eval from evidence.`,
        next,
      );
      return;
    }
    if (wording?.kind === 'description') {
      setNotesDraft(wording.value);
      appendChat(
        'peer',
        'Description updated (kept tight). Save to persist. Force rescore re-evals 1–5 from evidence (not a chat quiz).',
        next,
      );
      return;
    }
    if (/^rescore\b/i.test(text.trim()) || /^force rescore\b/i.test(text.trim())) {
      void runForceRescore();
      return;
    }
    // Default: short → card name override; longer → full AI ticket review
    if (text.length > 80 || text.includes('\n')) {
      const pack: CreatePack = {
        seed: titleDraft || task?.title || text.slice(0, 120),
        value: text,
        outcome: '',
        priority: '',
      };
      void (async () => {
        await applyApiReview(pack, {
          existing: { name: titleDraft.trim() || undefined },
          newInfo: text,
          forceReview: true,
        });
        appendChat(
          'peer',
          'Reviewed ticket from the API. Say name: … to rename, or Force rescore to re-eval 1–5 from evidence.',
          next,
        );
      })();
      return;
    }
    const name = text.trim().slice(0, 56);
    setTitleDraft(name);
    appendChat(
      'peer',
      `Card name → «${name}». Or prefix with description: for summary. Force rescore to re-eval the 1–5 from evidence.`,
      next,
    );
  };

  const buildEvidence = (scoredBy: 'manual' | 'agent'): TaskValueEvidence | null => {
    if (score === '' || score < 1 || score > 5) return existing;
    return {
      version: 1,
      axes,
      strategy_score: score,
      summary: scoreWhy.trim() || existing?.summary || 'Scored in task eval chat',
      resources: existing?.resources ?? [],
      scored_at: new Date().toISOString(),
      contradictions: existing?.contradictions,
      scored_by: scoredBy,
      work_justification: existing?.work_justification,
      completion_justification:
        completionJustification.trim() || existing?.completion_justification,
    };
  };

  const buildEval = (): TaskEval => ({
    ...evalState,
    completion_justification:
      completionJustification.trim() || evalState.completion_justification,
    updated_at: new Date().toISOString(),
  });

  const handleCreate = async () => {
    if (!epicId) {
      toast.error('Pick standalone (Ops inbox) or an epic (left panel or chat) before creating');
      setCreateStep('placement');
      return;
    }
    if (!(createPack.priority || createPriority).trim()) {
      toast.error('Priority is required — what happens if we don’t get this done?');
      return;
    }
    if (typeof score !== 'number' || score < 1 || score > 5) {
      toast.error('Score 1–5 is required before create');
      setCreateStep('score');
      return;
    }
    const titleTrim = titleDraft.trim();
    if (!titleTrim || titleTrim === 'Untitled task') {
      toast.error('Set a short name before creating');
      return;
    }
    const description = notesDraft.trim();
    if (!description) {
      toast.error('Add a value summary description before creating');
      return;
    }

    let nextEval = evalState;
    if (chatDraft.trim()) {
      nextEval = appendChat('user', cleanupUserPrompt(chatDraft));
      setChatDraft('');
    }

    const chosenEpic = epics.find((e) => e.id === epicId);
    setSaving(true);
    try {
      let dueLocal = dueDraft.trim();
      if (!dueLocal) {
        const applied = await applyApiReview(createPack, {
          forceReview: true,
          epicIdOverride: epicId,
        });
        dueLocal = applied.dueLocal;
      }
      const scheduleContext = await loadScheduleBoardContext(epicId);
      dueLocal = resolveCreateDue(scheduleContext, dueLocal || null, scheduleHorizon);
      setDueDraft(dueLocal);
      // Parse as local Y-M-D so we don't UTC-shift the calendar day
      const day = toDateOnly(dueLocal);
      const due = day
        ? (() => {
            const [y, m, d] = day.split('-').map(Number);
            const time = dueLocal.match(/T(\d{1,2}):(\d{2})/);
            return new Date(y, m - 1, d, time ? Number(time[1]) : 9, time ? Number(time[2]) : 0, 0, 0);
          })()
        : new Date(resolveCreateDue(scheduleContext, null, scheduleHorizon));
      if (Number.isNaN(due.getTime())) {
        toast.error('Schedule date is invalid');
        return;
      }
      let metadata: Record<string, unknown> = {
        origin: 'task_eval_chat',
        awaiting_system_eval: true,
        create_interview: true,
        create_planning: {
          priority_if_not_done: (createPack.priority || createPriority).trim(),
          epic_slug: chosenEpic?.slug ?? null,
          epic_title: chosenEpic?.title ?? null,
          standalone: chosenEpic?.slug === 'ops-inbox',
          strategy_score: score,
          score_why: scoreWhy.trim() || null,
          schedule_horizon: scheduleHorizon,
          due_why: dueWhy || null,
        },
      };
      const evidence: TaskValueEvidence = {
        version: 1,
        axes: emptyAxes(),
        strategy_score: score as number,
        summary:
          scoreWhy.trim() ||
          description.slice(0, 500) ||
          'Scored in create planning chat',
        resources: [],
        scored_at: new Date().toISOString(),
        scored_by: 'manual',
      };
      metadata = withTaskValueEvidence(metadata, evidence);
      const te: TaskEval = {
        ...nextEval,
        messages: [
          ...nextEval.messages,
          {
            id: newMessageId(),
            role: 'system',
            content: `Created after planning meeting (work → justify → done → priority → placement → score ${score}/5 on ${chosenEpic?.title ?? 'epic'}). Force rescore re-evals from evidence; MCP can deepen.`,
            at: new Date().toISOString(),
          },
        ],
        updated_at: new Date().toISOString(),
      };
      metadata = withTaskEval(metadata, te);

      const { error } = await supabase.from('tasks').insert({
        subscriber_id: null,
        contact_email: defaultContactEmail,
        task_type: 'custom',
        title: titleTrim,
        status: 'pending',
        due_at: due.toISOString(),
        source: 'manual',
        notes: description,
        metadata,
        sort_order: 100,
        strategy_score: score,
        epic_id: epicId,
        epic_placement: epicPlacement || 'suggested',
      });
      if (error) throw error;

      toast.success(
        chosenEpic
          ? `Task created on «${chosenEpic.title}» · score ${score}`
          : `Task created · score ${score}`,
      );
      onSaved();
      onClose();
    } catch (e: unknown) {
      reportUiError('tasks', 'create_via_eval_chat', e);
      toast.error(e instanceof Error ? e.message : 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (alsoMarkDone = false) => {
    if (isCreate) {
      await handleCreate();
      return;
    }
    if (!task) return;

    if (alsoMarkDone) {
      if (!completionJustification.trim()) {
        toast.error('Chat needs the business value of what completed before close.');
        startCloseOut();
        return;
      }
      if (closeStep !== 'ready') {
        toast.message('Finish the close-out chat first — value is captured in the thread.');
        setUiTab('chat');
        setClosing(true);
        return;
      }
    }

    setSaving(true);
    try {
      const evidence = buildEvidence('manual');
      const nextEval = buildEval();
      let metadata = withTaskEval(task.metadata, nextEval);
      if (evidence) {
        metadata = withTaskValueEvidence(metadata, evidence);
      }

      const patch: Record<string, unknown> = {
        metadata,
        updated_at: new Date().toISOString(),
      };

      if (evidence) {
        patch.strategy_score = evidence.strategy_score;
      }

      const titleTrim = titleDraft.trim();
      if (titleTrim && titleTrim !== (task.title ?? '')) {
        patch.title = titleTrim;
      }
      if (notesDraft !== (task.notes ?? '')) {
        patch.notes = notesDraft.trim() || null;
      }
      if (dueDraft) {
        const due = new Date(dueDraft);
        if (!Number.isNaN(due.getTime()) && due.toISOString() !== task.due_at) {
          patch.due_at = due.toISOString();
        }
      }
      if (epicId !== (task.epic_id ?? '')) {
        patch.epic_id = epicId || null;
      }
      if (epicPlacement !== (task.epic_placement ?? 'suggested')) {
        patch.epic_placement = epicId ? epicPlacement : null;
      }
      if (epicId && !task.epic_placement) {
        patch.epic_placement = epicPlacement;
      }

      if (alsoMarkDone) {
        patch.status = 'done';
        patch.completed_at = new Date().toISOString();
      }

      const { error } = await supabase.from('tasks').update(patch).eq('id', task.id);
      if (error) throw error;

      let spawned = 0;
      if (alsoMarkDone && followUpSeeds.length > 0) {
        const inbox = epics.find((e) => e.slug === 'ops-inbox') ?? epics[0];
        const due = new Date(defaultDueLocal());
        for (const seed of followUpSeeds) {
          const title = fallbackCreateName(seed);
          const { error: spawnErr } = await supabase.from('tasks').insert({
            subscriber_id: task.subscriber_id,
            contact_email: task.contact_email || defaultContactEmail,
            task_type: 'custom',
            title,
            status: 'pending',
            due_at: due.toISOString(),
            source: 'manual',
            notes: `Follow-up from closed task: ${task.title || task.id}\n\nSeed: ${seed}\n\nParent value: ${completionJustification.trim()}`,
            metadata: {
              origin: 'close_out_spawn',
              parent_task_id: task.id,
              awaiting_system_eval: true,
              create_interview: false,
            },
            sort_order: 100,
            strategy_score: null,
            epic_id: inbox?.id ?? task.epic_id ?? null,
            epic_placement: inbox || task.epic_id ? 'suggested' : null,
          });
          if (!spawnErr) spawned += 1;
        }
      }

      toast.success(
        alsoMarkDone
          ? spawned > 0
            ? `Task closed — value saved · ${spawned} follow-up${spawned === 1 ? '' : 's'} created`
            : 'Task closed — business value saved from chat'
          : 'Eval saved — conversation kept',
      );
      onSaved();
      onClose();
    } catch (e: unknown) {
      reportUiError('tasks', 'update_score_modal', e, { task_id: task.id });
      toast.error(e instanceof Error ? e.message : 'Failed to save eval');
    } finally {
      setSaving(false);
    }
  };

  // Create = two columns: live task facts (left) + planning chat (right).
  if (isCreate) {
    const chosenEpic = epics.find((e) => e.id === epicId);
    const canCreate =
      Boolean(epicId) &&
      Boolean((createPack.priority || createPriority).trim()) &&
      typeof score === 'number' &&
      score >= 1 &&
      score <= 5 &&
      Boolean(titleDraft.trim()) &&
      titleDraft.trim() !== 'Untitled task' &&
      Boolean(notesDraft.trim()) &&
      (dupCleared || similarTasks.length === 0);

    const interviewHint =
      createStep === 'seed'
        ? 'Paste a full dump — or answer one step'
        : createStep === 'value'
          ? 'Missing: value'
          : createStep === 'outcome'
            ? 'Missing: done'
              : createStep === 'priority'
              ? 'Missing: priority'
              : createStep === 'ai_questions'
                ? 'Corpus clarifier — answer updates task'
                : createStep === 'dup_check'
                ? 'Similar tasks — clarify or force'
                : createStep === 'placement'
                  ? 'Confirm epic'
                  : createStep === 'score'
                    ? 'Score 1–5'
                    : 'Enrich · then Create';

    const scoreLabel =
      typeof score === 'number'
        ? STRATEGY_SCORE_LABELS[score] ?? `Score ${score}`
        : 'Working…';

    return (
      <ModalOverlay>
        <ModalPanel maxWidthClass="max-w-7xl" titleId="task-create-chat-title" className="overflow-hidden">
          <ModalHeader title="New task" onClose={onClose} titleId="task-create-chat-title" />
          <ModalBody className="space-y-3">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-0 lg:min-h-[32rem]">
              {/* Left — live facts */}
              <section className="lg:col-span-4 rounded-xl border border-violet-200 dark:border-violet-800/60 bg-violet-50/40 dark:bg-violet-950/20 p-4 space-y-3 overflow-y-auto max-h-[min(70vh,40rem)]">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-violet-950 dark:text-violet-100">
                    What we know
                  </h3>
                  <span
                    className={`inline-flex items-center gap-1 text-sm font-bold tabular-nums px-2.5 py-1 rounded-full ${
                      typeof score === 'number' && isHighValueStrategyScore(score)
                        ? 'bg-violet-100 text-violet-900 dark:bg-violet-900/50 dark:text-violet-100'
                        : typeof score === 'number'
                          ? 'bg-white/90 dark:bg-gray-900/50 text-gray-800 dark:text-gray-100 border border-violet-100 dark:border-violet-900/50'
                          : 'bg-white/60 dark:bg-gray-900/40 text-gray-500 border border-dashed border-violet-200 dark:border-violet-800'
                    }`}
                    title="Potential score — fills in as planning chat progresses"
                  >
                    {typeof score === 'number' ? `${score} · ${scoreLabel}` : '— · unscored'}
                  </span>
                </div>

                <label className="block">
                  <span className={labelClass}>Name</span>
                  <input
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    maxLength={56}
                    placeholder="Short card title — e.g. Grant Jesse Supabase access"
                    className={fieldClass}
                  />
                </label>

                <label className="block">
                  <span className={labelClass}>Description</span>
                  <textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={3}
                    maxLength={520}
                    placeholder="Tight: value · Done · If not"
                    className={`${fieldClass} resize-y text-sm`}
                  />
                </label>

                <div className="block">
                  <span className={labelClass}>Attached to (epic)</span>
                  {epicCreateStep === 'form' ? (
                    <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/20 p-3 space-y-3 mt-1.5">
                      <label className="block">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          Epic name
                        </span>
                        <input
                          value={epicDraftTitle}
                          onChange={(e) => setEpicDraftTitle(e.target.value)}
                          placeholder="e.g. Sept Microconf"
                          className={`${fieldClass} mt-1`}
                          autoFocus
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          Description
                        </span>
                        <textarea
                          value={epicDraftDescription}
                          onChange={(e) => setEpicDraftDescription(e.target.value)}
                          rows={3}
                          placeholder="What this plan covers / done looks like"
                          className={`${fieldClass} mt-1 resize-y text-sm`}
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          Deadline
                        </span>
                        <input
                          type="date"
                          value={epicDraftDeadline}
                          onChange={(e) => setEpicDraftDeadline(e.target.value)}
                          className={`${fieldClass} mt-1`}
                        />
                      </label>
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={cancelCreateEpicForm}
                          disabled={epicSaving}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg text-gray-600 dark:text-gray-300 hover:bg-white/80 dark:hover:bg-gray-800"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => void submitCreateEpicForm()}
                          disabled={
                            epicSaving ||
                            epicDraftTitle.trim().length < 2 ||
                            isEpicTriggerPhrase(epicDraftTitle)
                          }
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                        >
                          {epicSaving ? 'Creating…' : 'Create epic'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mt-1.5">
                        <select
                          value={epicId}
                          onChange={(e) => {
                            const nextId = e.target.value;
                            setEpicId(nextId);
                            const ep = epics.find((x) => x.id === nextId);
                            if (!nextId) {
                              setEpicPlacement('suggested');
                              return;
                            }
                            setEpicPlacement(ep?.slug === 'ops-inbox' ? 'suggested' : 'tentative');
                            if (createStep === 'placement') {
                              setCreateStep('score');
                              appendChat(
                                'peer',
                                `Epic set in UI to «${ep?.title ?? 'epic'}».\n\n${SCORE_RUBRIC}`,
                              );
                            }
                          }}
                          className={`${fieldClass} flex-1 min-w-0`}
                        >
                          <option value="">— pick in chat or here —</option>
                          {activeEpicsForMenu(epics).map((e) => (
                            <option key={e.id} value={e.id}>
                              {e.title}
                              {e.slug === 'ops-inbox' ? ' (standalone inbox)' : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          title="Create a new epic"
                          onClick={() => {
                            if (epicCreateStep === 'form') {
                              cancelCreateEpicForm();
                              return;
                            }
                            if (epicCreateStep !== 'idle') return;
                            openCreateEpicForm();
                          }}
                          disabled={
                            (epicCreateStep !== 'idle' && epicCreateStep !== 'form') ||
                            saving ||
                            epicSaving
                          }
                          className="inline-flex items-center justify-center h-10 w-10 shrink-0 rounded-lg border border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/40 disabled:opacity-40"
                        >
                          <Plus className="w-4 h-4" aria-hidden />
                          <span className="sr-only">Add epic</span>
                        </button>
                      </div>
                      {chosenEpic ? (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                          Placement: {epicPlacement}
                          {chosenEpic.target_date ? ` · deadline ${chosenEpic.target_date}` : ''}
                        </p>
                      ) : null}
                    </>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <label className="block min-w-0">
                    <span className={labelClass}>
                      Schedule
                      {scheduleHorizon ? (
                        <span className="ml-1 font-normal text-gray-500">
                          · {scheduleHorizon}
                        </span>
                      ) : null}
                    </span>
                    <input
                      type="datetime-local"
                      value={dueDraft}
                      onChange={(e) => setDueDraft(e.target.value)}
                      className={fieldClass}
                    />
                  </label>
                  <label className="block min-w-0">
                    <span className={labelClass}>Score (1–5)</span>
                    <select
                      value={typeof score === 'number' ? score : ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (!raw) {
                          setScore('');
                          return;
                        }
                        const n = Number(raw);
                        setScore(n);
                        if (createStep === 'score') {
                          setCreateStep('confirm');
                          appendChat(
                            'peer',
                            `Score ${n}/5 set in UI. Confirm name + description on the left, then Create — or refine in chat.`,
                          );
                        }
                      }}
                      className={fieldClass}
                    >
                      <option value="">— working —</option>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>
                          {n} · {STRATEGY_SCORE_LABELS[n]}
                        </option>
                      ))}
                    </select>
                  </label>
                  {dueWhy ? (
                    <p className="col-span-2 text-[11px] text-gray-500 dark:text-gray-400 -mt-1">
                      {dueWhy}
                    </p>
                  ) : null}
                </div>

                <label className="block">
                  <span className={labelClass}>Placement</span>
                  <select
                    value={epicPlacement}
                    onChange={(e) => setEpicPlacement(e.target.value as EpicPlacement)}
                    disabled={!epicId}
                    className={`${fieldClass} disabled:opacity-50`}
                  >
                    {EPIC_PLACEMENTS.map((p) => (
                      <option key={p} value={p}>
                        {EPIC_PLACEMENT_LABELS[p]}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className={labelClass}>If we don’t (priority)</span>
                  <textarea
                    value={createPack.priority || createPriority}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCreatePriority(v);
                      setCreatePack((p) => ({ ...p, priority: v }));
                    }}
                    rows={2}
                    placeholder="Cost of delay"
                    className={`${fieldClass} resize-y text-sm`}
                  />
                </label>
              </section>

              {/* Right — planning chat */}
              <section className="lg:col-span-8 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/40 flex flex-col min-h-[28rem] max-h-[min(70vh,40rem)] overflow-hidden relative">
                {corpusLooking ? (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/80 dark:bg-gray-900/80 backdrop-blur-[1px]">
                    <Loader2 className="w-8 h-8 animate-spin text-violet-600" aria-hidden />
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                      Evaluating on the server…
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Hang on — this can take a few seconds</p>
                  </div>
                ) : null}
                <div className="px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700 shrink-0">
                  <p className="text-xs font-medium text-violet-700 dark:text-violet-300">
                    {interviewHint}
                  </p>
                </div>
                <div
                  ref={chatScrollRef}
                  className="flex-1 overflow-y-auto space-y-2 px-4 py-3"
                >
                  {evalState.messages.length === 0 ? (
                    <p className="text-xs italic text-gray-500 dark:text-gray-400 py-10 text-center px-4">
                      Paste a full planning dump (value / done / priority) — I’ll only ask what’s
                      missing, match an epic, and push back on similar open tasks.
                    </p>
                  ) : (
                    evalState.messages.map((m) => (
                      <div
                        key={m.id}
                        className={`rounded-lg px-3 py-2 text-sm leading-relaxed ${
                          m.role === 'user'
                            ? 'bg-violet-50 dark:bg-violet-950/40 text-gray-900 dark:text-gray-100 ml-8'
                            : m.role === 'peer'
                              ? 'bg-emerald-50 dark:bg-emerald-950/30 text-gray-800 dark:text-gray-100 mr-8'
                              : 'bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300 text-xs'
                        }`}
                      >
                        <span className="block text-[10px] uppercase tracking-wide opacity-60 mb-0.5">
                          {m.role}
                        </span>
                        <span className="whitespace-pre-wrap break-words">{m.content}</span>
                      </div>
                    ))
                  )}
                  <div ref={createChatEndRef} />
                </div>
                <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 px-3 py-3">
                  <div className="flex items-end gap-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-violet-500">
                    <textarea
                      value={chatDraft}
                      onChange={(e) => setChatDraft(e.target.value)}
                      rows={2}
                      placeholder={
                        createStep === 'seed'
                          ? 'Full dump or what’s the work…'
                          : createStep === 'value'
                            ? 'Value — $ / goals / network…'
                            : createStep === 'outcome'
                              ? 'Done looks like…'
                              : createStep === 'priority'
                                ? 'If we don’t get this done…'
                                : createStep === 'ai_questions'
                                  ? 'Answer clarifier (or skip)…'
                                : createStep === 'dup_check'
                                  ? 'clarify: … or force create'
                                  : createStep === 'placement'
                                    ? 'yes / standalone / epic number…'
                                    : createStep === 'score'
                                      ? 'Enter 1–5…'
                                      : 'Enrich — name: … / description: …'
                      }
                      className="flex-1 resize-none border-0 bg-transparent p-1 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-0 min-h-[2.75rem]"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendChat();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleSendChat}
                      disabled={!chatDraft.trim() || corpusLooking || saving}
                      className="shrink-0 self-end mb-0.5 p-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40"
                      title="Send"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </ModalBody>
          <ModalFooter className="flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            {canCreate ? (
              <button
                type="button"
                onClick={() => void handleCreate()}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
              >
                {saving ? 'Creating…' : 'Create'}
              </button>
            ) : (
              <button
                type="button"
                disabled
                title="Finish planning (epic + score 1–5 + name + description + priority)"
                className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              >
                {!epicId
                  ? 'Attach epic…'
                  : typeof score !== 'number'
                    ? 'Score still working…'
                    : createStep !== 'confirm'
                      ? 'Finish planning chat…'
                      : 'Finish planning…'}
              </button>
            )}
          </ModalFooter>
        </ModalPanel>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay>
      <ModalPanel
        maxWidthClass="max-w-7xl"
        titleId="task-score-modal-title"
        className="overflow-hidden sm:h-[min(88vh,820px)] sm:max-h-[min(88vh,820px)]"
      >
        <ModalHeader
          title={intent === 'complete' || closing ? 'Close out task' : 'Task eval'}
          onClose={onClose}
          titleId="task-score-modal-title"
        />
        <ModalBody className="!overflow-hidden flex flex-col space-y-3 py-3">
          <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 dark:border-gray-700 pb-2 shrink-0">
            {(
              [
                { id: 'chat' as const, label: 'Chat' },
                { id: 'details' as const, label: 'Details' },
              ] as const
            ).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  if ((closing || intent === 'complete') && tab.id === 'details') {
                    toast.message('Close-out stays in chat until business value is captured');
                    setUiTab('chat');
                    return;
                  }
                  setUiTab(tab.id);
                }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  uiTab === tab.id
                    ? 'bg-violet-600 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
            {task?.transcript_id ? (
              <Link
                to={`/transcripts/${task.transcript_id}`}
                onClick={onClose}
                className="ml-auto inline-flex items-center gap-1 text-xs text-teal-700 dark:text-teal-300 hover:underline"
              >
                <FileText className="w-3.5 h-3.5" aria-hidden />
                Transcript
                <ExternalLink className="w-3 h-3 opacity-70" aria-hidden />
              </Link>
            ) : null}
          </div>

          {uiTab === 'chat' ? (
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-3">
              <section className="lg:col-span-7 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/40 flex flex-col min-h-0 overflow-hidden relative">
                {corpusLooking ? (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/80 dark:bg-gray-900/80 backdrop-blur-[1px]">
                    <Loader2 className="w-8 h-8 animate-spin text-violet-600" aria-hidden />
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                      Evaluating on the server…
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Hang on — this can take a few seconds</p>
                  </div>
                ) : null}
                <div
                  ref={chatScrollRef}
                  className="flex-1 min-h-0 overflow-y-auto space-y-2 px-4 py-3"
                >
                  {evalState.messages.length === 0 ? (
                    <p className="text-xs italic text-gray-500 dark:text-gray-400 py-8 text-center">
                      No thread yet. Dump thoughts, ask what value this pulls, or stress-test the
                      score.
                    </p>
                  ) : (
                    evalState.messages.map((m) => (
                      <div
                        key={m.id}
                        className={`rounded-lg px-3 py-2 text-sm leading-relaxed ${
                          m.role === 'user'
                            ? 'bg-violet-50 dark:bg-violet-950/40 text-gray-900 dark:text-gray-100 ml-6'
                            : m.role === 'peer'
                              ? 'bg-emerald-50 dark:bg-emerald-950/30 text-gray-800 dark:text-gray-100 mr-6'
                              : 'bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300 text-xs'
                        }`}
                      >
                        <span className="block text-[10px] uppercase tracking-wide opacity-60 mb-0.5">
                          {m.role}
                        </span>
                        <span className="whitespace-pre-wrap break-words">{m.content}</span>
                      </div>
                    ))
                  )}
                  <div ref={createChatEndRef} />
                </div>

                <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 px-3 py-3 space-y-2">
                  {(closing || intent === 'complete') && completionJustification.trim() ? (
                    <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50/60 dark:bg-emerald-950/20 px-3 py-2 max-h-20 overflow-y-auto">
                      <p className="text-[10px] uppercase tracking-wide font-medium text-emerald-800 dark:text-emerald-200 mb-1">
                        Business value (from chat)
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">
                        {completionJustification}
                      </p>
                      {followUpSeeds.length > 0 ? (
                        <p className="text-xs text-emerald-800/80 dark:text-emerald-200/80 mt-1">
                          Follow-ups queued: {followUpSeeds.length}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="flex items-end gap-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-violet-500">
                    <textarea
                      value={chatDraft}
                      onChange={(e) => setChatDraft(e.target.value)}
                      rows={2}
                      placeholder={
                        closing || intent === 'complete'
                          ? closeStep === 'ask_value'
                            ? 'Business value + what was completed…'
                            : closeStep === 'ask_spawn'
                              ? 'Yes or no — create follow-up tasks?'
                              : closeStep === 'collect_spawns'
                                ? 'One follow-up per line…'
                                : 'Optional — refine value or mention more work…'
                          : 'Message…'
                      }
                      autoFocus
                      className="flex-1 resize-none border-0 bg-transparent p-1 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-0 min-h-[2.5rem]"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendChat();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleSendChat}
                      disabled={!chatDraft.trim() || corpusLooking || saving}
                      className="shrink-0 self-end mb-0.5 p-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40"
                      title="Send"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </section>

              <section className="lg:col-span-5 rounded-xl border border-violet-200 dark:border-violet-800/60 bg-violet-50/50 dark:bg-violet-950/20 p-4 space-y-3 min-h-0 overflow-y-auto">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1.5 text-base font-bold tabular-nums px-3 py-1.5 rounded-full ${
                      typeof currentScore === 'number' && isHighValueStrategyScore(currentScore)
                        ? 'bg-violet-100 text-violet-900 dark:bg-violet-900/50 dark:text-violet-100'
                        : 'bg-white/90 dark:bg-gray-900/50 text-gray-800 dark:text-gray-100 border border-violet-100 dark:border-violet-900/50'
                    }`}
                  >
                    {currentScore ?? '—'}
                    {typeof currentScore === 'number'
                      ? ` · ${STRATEGY_SCORE_LABELS[currentScore] ?? ''}`
                      : ' Unscored'}
                    {showHand ? <Hand className="w-4 h-4 opacity-70" aria-label="Manual" /> : null}
                  </span>
                  {task?.epic?.title ? (
                    <span className="text-xs font-medium px-2 py-1 rounded-md bg-indigo-50 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200">
                      {task.epic.title}
                      {task.epic_placement ? ` · ${task.epic_placement}` : ''}
                    </span>
                  ) : null}
                </div>

                <p className="text-xs text-violet-800/80 dark:text-violet-200/70 leading-relaxed">
                  Due:{' '}
                  <span className="font-medium text-gray-800 dark:text-gray-100">
                    {task?.due_at ? new Date(task.due_at).toLocaleString() : '—'}
                  </span>
                </p>

                <div className="space-y-2 border-t border-violet-200/80 dark:border-violet-800/50 pt-3">
                  <h3 className="text-sm font-semibold text-violet-950 dark:text-violet-100">Evidence</h3>
                  {existing ? (
                    <>
                      <div className="flex flex-wrap gap-2">
                        {(Object.keys(AXIS_LABELS) as Array<keyof typeof AXIS_LABELS>).map((key) => (
                          <span
                            key={key}
                            className="text-xs font-medium px-2.5 py-1 rounded-md bg-white/90 dark:bg-gray-900/50 text-violet-900 dark:text-violet-100 border border-violet-100 dark:border-violet-900/50"
                          >
                            {AXIS_LABELS[key].short}{' '}
                            <span className="font-bold tabular-nums">{existing.axes[key]}/3</span>
                          </span>
                        ))}
                      </div>
                      {existing.summary ? (
                        <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-100 whitespace-pre-wrap">
                          {existing.summary}
                        </p>
                      ) : (
                        <p className="text-xs italic text-gray-500">No summary yet.</p>
                      )}
                      {existing.resources.length > 0 ? (
                        <ul className="space-y-2">
                          {existing.resources.map((resource, index) => (
                            <li
                              key={`${resource.kind}-${resource.label}-${index}`}
                              className="text-xs rounded-lg bg-white/80 dark:bg-gray-900/40 border border-violet-100 dark:border-violet-900/40 px-3 py-2"
                            >
                              <span className="font-medium text-gray-900 dark:text-white">
                                {resource.label}
                              </span>
                              {resource.note ? ` — ${resource.note}` : ''}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs italic text-gray-500">No linked resources yet.</p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-violet-800/80 dark:text-violet-200/70">
                      Empty until peers/MCP attach sources.
                    </p>
                  )}
                </div>
              </section>
            </div>
          ) : (
            <section className="flex-1 min-h-0 w-full overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/40 p-4 sm:p-5">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <label className="block lg:col-span-5">
                  <span className={labelClass}>Name</span>
                  <input
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    maxLength={56}
                    placeholder="Short card title for the board tab"
                    className={fieldClass}
                  />
                </label>
                <label className="block lg:col-span-3">
                  <span className={labelClass}>Due</span>
                  <input
                    type="datetime-local"
                    value={dueDraft}
                    onChange={(e) => setDueDraft(e.target.value)}
                    className={fieldClass}
                  />
                </label>
                <label className="block lg:col-span-2">
                  <span className={labelClass}>Epic</span>
                  <select
                    value={epicId}
                    onChange={(e) => setEpicId(e.target.value)}
                    className={fieldClass}
                  >
                    <option value="">—</option>
                    {epics.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.title}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block lg:col-span-2">
                  <span className={labelClass}>Placement</span>
                  <select
                    value={epicPlacement}
                    onChange={(e) => setEpicPlacement(e.target.value as EpicPlacement)}
                    disabled={!epicId}
                    className={`${fieldClass} disabled:opacity-50`}
                  >
                    {EPIC_PLACEMENTS.map((p) => (
                      <option key={p} value={p}>
                        {EPIC_PLACEMENT_LABELS[p]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block lg:col-span-12">
                  <span className={labelClass}>Description</span>
                  <textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={5}
                    maxLength={520}
                    placeholder="Tight: value · Done · If not"
                    className={`${fieldClass} resize-y min-h-[6rem]`}
                  />
                </label>
              </div>
            </section>
          )}
        </ModalBody>

        <ModalFooter className="flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
          {intent === 'complete' || closing ? (
            <>
              {uiTab === 'details' ? (
                <button
                  type="button"
                  onClick={() => setUiTab('chat')}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Back to chat
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => void handleSave(true)}
                disabled={saving || closeStep !== 'ready' || !completionJustification.trim()}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving
                  ? 'Saving…'
                  : closeStep !== 'ready'
                    ? 'Finish close-out chat…'
                    : followUpSeeds.length > 0
                      ? `Save value, close & spawn ${followUpSeeds.length}`
                      : 'Save value & close'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => void runForceRescore()}
                disabled={saving || corpusLooking}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-violet-300 dark:border-violet-700 text-violet-800 dark:text-violet-200 hover:bg-violet-50 dark:hover:bg-violet-950/40 disabled:opacity-50"
              >
                {saving || corpusLooking ? 'Rescoring…' : 'Force rescore'}
              </button>
              <button
                type="button"
                onClick={() => {
                  startCloseOut();
                  toast.message('Close-out in chat — capture business value of what completed');
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
              >
                Close out…
              </button>
              <button
                type="button"
                onClick={() => void handleSave(false)}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
        </ModalFooter>
      </ModalPanel>
    </ModalOverlay>
  );
};

export default TaskScoreModal;
